import { LitElement, css, html, nothing } from 'lit'
import { customElement, property, queryAssignedElements } from 'lit/decorators.js'
import { styleMap } from 'lit/directives/style-map.js'
import { MutationController } from '@lit-labs/observers/mutation-controller.js';
import { mustacheTemplateContent } from '../directives/mustacheTemplateContent';
import { HasSlotController } from '../controllers/HasSlotController';

//import UPLOAD_SVG from '../shared-assets/upload.svg'

type EPFileUploadVariant = 'tooltip' | 'flex-right'

@customElement('ep-file-upload')
export class EPFileUpload extends LitElement {
  private hasSlotController = new HasSlotController(this, 'leading-icon', 'trailing-icon');
  private inputObserver = new MutationController(this, { 
    target: null,
    callback: () => this._handleInputObservation(),
    config: { attributes: true, characterData: false, subtree: false, }
  })

  @property({ reflect: true })
  variant: EPFileUploadVariant = 'tooltip'

  @property({ type: Boolean, reflect: true })
  disabled: boolean = false;

  @queryAssignedElements({ slot: 'input', selector: 'input[type=file]', flatten: true })
  slottedInput?: HTMLInputElement[];

  @queryAssignedElements({ slot: 'file-template', selector: 'template' })
  slottedTemplate?: HTMLTemplateElement[];

  @queryAssignedElements({ slot: 'fake-input' })
  slottedFakeInput?: HTMLElement[];

  @property({ type: Boolean, attribute: 'hover-self', reflect: true })
  fileDraggedOverSelf: boolean = false;

  @property({ type: Boolean, attribute: 'hover-body', reflect: true })
  fileDraggedOverBody: boolean = false;

  get input(): HTMLInputElement | undefined {
    if (this.slottedInput && this.slottedInput.length >= 1) {
      return this.slottedInput[0];
    }
    return undefined;
  }

  get files(): File[] {
    if (this.slottedInput && this.slottedInput.length >= 1) {
      return Array.from(this.slottedInput[0].files ?? []);
    }
    return [];
  }

  get fakeInput(): HTMLElement | undefined {
    if (this.slottedFakeInput && this.slottedFakeInput.length >= 1) {
      return this.slottedFakeInput[0];
    }
    return undefined
  }

  get hasFakeInputSlotted(): boolean {
    return this.hasSlotController.test('fake-input');
  }

  render() {
    return html`
      <ep-contents @drop=${this._handleFileDropped} @dragover=${this._handleSelfDragEnterLeave} @dragleave=${this._handleSelfDragEnterLeave}>
        <slot name="file-template"></slot>
        <slot name="input" @change=${() => this.requestUpdate()} @slotchange=${this._handleInputSlotChange}
          style=${styleMap({ display: this.hasFakeInputSlotted ? 'none' : 'contents' })}></slot>
        <slot name="fake-input" @click=${() => this.input?.click()}></slot>
        ${
          this.variant === 'tooltip'
          ? html`
            ${
              this.files.length > 0
              ? html`
                <overlay-trigger placement="top" style="font-size: initial;">
                  <span class="attach-message" data-attached slot="trigger">${this.files.length} file${this.files.length > 1 ? 's' : ''} attached</span>
                  <ep-tooltip slot="hover-content" color="light" placement="top" style="--max-width: 999px;">
                    ${
                      this.slottedTemplate && this.slottedTemplate.length >= 1
                      ? this.files.map(file => html`${mustacheTemplateContent(this.slottedTemplate![0], file)}`)
                      : ''
                    }
                  </ep-tooltip>
                </overlay-trigger>
              `
              : html`
              <span class="attach-message">No files attached</span>
              `
            }
          `
          : nothing
        }
        ${
          this.variant === 'flex-right'
          ? html`
            <div part="flex-right-container">
              ${
                this.slottedTemplate && this.slottedTemplate.length >= 1
                ? this.files.map(file => html`
                  <span style="display: contents;" @cancel=${(e: CustomEvent) => this._handleCancelEvent(e, file)}>
                  ${mustacheTemplateContent(this.slottedTemplate![0], file)}
                  </span>
                `)
                : ''
              }
            </div>
          `
          : nothing
        }
      </ep-contents>
    `
  }

  private _handleInputSlotChange() {
    if (this.input) {
      this.inputObserver.observe(this.input);
    }
  }

  private _handleInputObservation() {
    //console.log('did something about this change?', this.input)
    if (this.input && this.fakeInput) {
      // If source input becomes disabled, we should be disabled
      this.disabled = this.input.disabled;
      if (this.input.disabled) {
        this.fakeInput.setAttribute('disabled', '');
      }
      else {
        this.fakeInput.removeAttribute('disabled');
      }
    }
  }

  private _handleCancelEvent(e: CustomEvent, file: File) {
    e.preventDefault();
    e.stopPropagation();
    this.removeFileFromFiles(file);
    this.requestUpdate();
  }

  private removeFileFromFiles(removal: File) {
    console.log('removeFileFromFiles?', removal)
    const dt = new DataTransfer();
    const index = this.files.findIndex(f => f === removal);
    // Check for undefined
    if (this.input && index >= 0) {
      // Copy files that aren't `removal` to "dt"
      for (let file of this.files) {
        if (file !== removal) {
          dt.items.add(file);
        }
      }
      // Replace input files
      this.input.files = dt.files;
    }
  }

  private addFileToFiles(addition: File) {
    console.log('addFileToFiles?', addition)
    if (this.input) {
      const dt = new DataTransfer();
      // Copy old files over
      for (let file of this.files) {
        dt.items.add(file);
      }
      // Add new file
      dt.items.add(addition);
      // Replace input files
      this.input.files = dt.files;
    }
  }

  private _handleSelfDragEnterLeave(event: DragEvent) {
    //console.log((event.target as HTMLElement)?.tagName, event.type, this.fileDraggedOverSelf, event.dataTransfer?.effectAllowed, event);
    event.preventDefault();
    const DRAG_LEAVE = event.type === 'dragleave';
    this.fileDraggedOverSelf = !DRAG_LEAVE;
  }

  private _handleBodyDragEnterLeave = ((event: DragEvent) => {
    //console.log((event.target as HTMLElement)?.tagName, event.type, this.fileDraggedOverSelf, event);
    event.preventDefault();
    const DRAG_LEAVE = event.type === 'dragleave';
    this.fileDraggedOverBody = !DRAG_LEAVE;
  }).bind(this);

  private _handleFileDropped(event: DragEvent) {
    event.preventDefault();
    // Check for undefined
    if (this.input && event.type === 'drop' && event.dataTransfer?.files instanceof FileList) {
      const attachments = event.dataTransfer.files
      // Clear old attachments
      this.input.value = '';
      // Copy new attachments
      for(let file of attachments) {
        this.addFileToFiles(file);
      }
      // Disable border
      setTimeout(() => {
        this.fileDraggedOverBody = false;
        this.fileDraggedOverSelf = false;
      }, 110);
    }
    //console.log(event)
  }

  public override connectedCallback() {
    super.connectedCallback()
    document.addEventListener('dragover', this._handleBodyDragEnterLeave);
    document.addEventListener('dragleave', this._handleBodyDragEnterLeave);
  }

  public override disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('dragover', this._handleBodyDragEnterLeave);
    document.removeEventListener('dragleave', this._handleBodyDragEnterLeave);
  }

  static styles = css`
    :host {
      padding: 4px;
      border: 2px solid transparent;
      border-radius: 4px;
      background-color: transparent;
      transition: border-color 100ms, background-color 100ms;
    }
    :host, file-attachment {
      display: inline-flex;
      gap: 8px;
      align-items: center;
      box-sizing: border-box;
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
    }

    @keyframes pulse {
      from {
        border-color: rgb(213, 213, 213);
      }
      to {
        border-color: rgb(20, 122, 243);
      }
    }

    :host([hover-self]) {
      border-color: rgb(20, 122, 243);
      background-color: rgba(20, 122, 243, 0.1);
    }

    :host([hover-body]:not([hover-self])) {
      border-color: rgb(213, 213, 213);
      border-style: dashed;
      animation-name: pulse;
      animation-duration: 500ms;
      animation-iteration-count: infinite;
      animation-direction: alternate;
      animation-timing-function: linear;
    }

    .attach-message {
      font-family: var(--font-prose);
      font-size: 12px;
      font-weight: 600;
      color: rgb(87, 96, 106);
      text-align: left;
      user-select: none;
    }

    .attach-message[data-attached]:hover {
      /* cursor: pointer; */
      text-decoration: underline;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-file-upload': EPFileUpload
  }
}
